import React, { useState } from 'react';
import { CountryProfileFilters, Taxonomies } from '../../../types';
import Button from '../../layout/button/button.component';
import { Cell, Grid } from '../../layout/grid/grid.component';
import PageSection from '../../layout/page-section/page-section.component';
import TaxonomyListBox from '../taxonomy-list-box/taxonomy-list-box.component';

import {
    filterButtonPadClassName,
    filterCountryRegionToggleButtonClassName,
    filterCountryRegionOptionsContainerClassName,
    filterCountryRegionSelectAllCountriesContainerClassName,
    filterCountryRegionToggleClassName
} from './taxonomy-filters.style';
import { sameArrayData } from '../../../utils';
import { LABEL_FOR_COUNTRIES_SEARCH_FIELD } from '../../../constants';

interface FiltersProps {
    taxonomies: Taxonomies;
    filters: CountryProfileFilters;
    onChangeFilters(newFilters: CountryProfileFilters): void;
}

const TaxonomyFilters: React.FC<FiltersProps> = ({
    taxonomies,
    filters,
    onChangeFilters
}) => {
    const [filterByRegion, changeFilterByRegion] = useState<boolean>(false);
    const [filterCountryIds, changeFilterCountryIds] = useState<number[]>(filters.countryIds);
    const [filterRegionIds, changeFilterRegionIds] = useState<number[]>(filters.regionIds);
    const [filterTypeOfLegislatedQuotaIds, changeFilterTypeOfLegislatedQuotaIds] = useState<number[]>(filters.typeOfQuotaIds);
    const [filterElectoralSystemIds, changeFilterElectoralSystemIds] = useState<number[]>(filters.electoralSystemIds);
    const [filterLegalSourcesIds, changeFilterLegalSourcesIds] = useState<number[]>(filters.legalSourcesIds);

    const allCountriesSelected: boolean = filterCountryIds.length === taxonomies.countries.length;
    const theme = filters.localLevel ? 'green' : 'blue';

    const handleClickApplyFilters = () => {
        onChangeFilters({
            ...filters,
            countryIds: filterCountryIds,
            typeOfQuotaIds: filterTypeOfLegislatedQuotaIds,
            electoralSystemIds: filterElectoralSystemIds,
            legalSourcesIds: filterLegalSourcesIds,
            regionIds: filterRegionIds
        });
    };

    const handleClickCountryRegionToggle = (event: React.MouseEvent) => {
        event.preventDefault();

        if (filterByRegion) {
            changeFilterRegionIds([]);
        }
        else {
            changeFilterCountryIds([]);
        }

        changeFilterByRegion(!filterByRegion);
    };

    const handleChangeSelectAllCountriesCheckbox = (checked: boolean) => {
        if (checked) {
            changeFilterCountryIds(taxonomies.countries.map(countryTaxonomy => countryTaxonomy.id));
        }
        else {
            changeFilterCountryIds([]);
        }
    };

    const countryRegionToggleButton = (
        <div className={ filterCountryRegionOptionsContainerClassName }>
            <div className={ filterCountryRegionSelectAllCountriesContainerClassName }>
                { !filterByRegion && (
                    <span onClick={ () => handleChangeSelectAllCountriesCheckbox(!allCountriesSelected) }>
                        { allCountriesSelected ? 'Unselect' : 'Select' } all countries
                    </span>
                ) }
            </div>
            <div className={ filterCountryRegionToggleClassName }>
                <button
                    type='button'
                    className={ filterCountryRegionToggleButtonClassName }
                    onClick={ handleClickCountryRegionToggle }
                    children={ `Select by ${filterByRegion ? 'country' : 'region'} >` }/>
            </div>
        </div>
    );

    const hasChanges = (
        !sameArrayData(filterCountryIds, filters.countryIds)
        || !sameArrayData(filterRegionIds, filters.regionIds)
        || !sameArrayData(filterTypeOfLegislatedQuotaIds, filters.typeOfQuotaIds)
        || !sameArrayData(filterElectoralSystemIds, filters.electoralSystemIds)
        || !sameArrayData(filterLegalSourcesIds, filters.legalSourcesIds)
    );

    return (
        <PageSection title='Select parameters for own analysis' theme={ theme }>
            <Grid>
                <Cell size={ 12 } sizeTablet={ 6 } sizeDesktop={ 3 }>
                    { filterByRegion ? (
                        <TaxonomyListBox
                            title='Region'
                            taxonomyType='regions'
                            taxonomies={ taxonomies.regions }
                            selectedIds={ filterRegionIds }
                            onChange={ changeFilterRegionIds }
                            children={ countryRegionToggleButton }/>
                    ) : (
                        <TaxonomyListBox
                            title='Country'
                            taxonomyType='countries'
                            taxonomies={ taxonomies.countries }
                            selectedIds={ filterCountryIds }
                            onChange={ changeFilterCountryIds }
                            searchField={ LABEL_FOR_COUNTRIES_SEARCH_FIELD }
                            grouped
                            children={ countryRegionToggleButton }/>
                    ) }
                </Cell>
                <Cell size={ 12 } sizeTablet={ 6 } sizeDesktop={ 3 }>
                    <TaxonomyListBox
                        title='Type of Legislated Quota'
                        taxonomyType='typesOfQuota'
                        taxonomies={ taxonomies.typesOfQuota }
                        selectedIds={ filterTypeOfLegislatedQuotaIds }
                        onChange={ changeFilterTypeOfLegislatedQuotaIds }/>
                </Cell>
                <Cell size={ 12 } sizeTablet={ 6 } sizeDesktop={ 3 }>
                    <TaxonomyListBox
                        title='Electoral System'
                        taxonomyType='electoralSystemTypes'
                        taxonomies={ taxonomies.electoralSystemTypes }
                        selectedIds={ filterElectoralSystemIds }
                        onChange={ changeFilterElectoralSystemIds }/>
                </Cell>
                <Cell size={ 12 } sizeTablet={ 6 } sizeDesktop={ 3 }>
                    <TaxonomyListBox
                        title='Legal Sources'
                        taxonomyType='legalSources'
                        taxonomies={ taxonomies.legalSources }
                        selectedIds={ filterLegalSourcesIds }
                        onChange={ changeFilterLegalSourcesIds }/>
                </Cell>
            </Grid>
            <div className={ filterButtonPadClassName }>
                <Button onClick={ handleClickApplyFilters } disabled={ !hasChanges }>
                    Confirm Selection
                </Button>
            </div>
        </PageSection>
    );
};

export default TaxonomyFilters;
